import { Activity, memo, useLayoutEffect, useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import { MenuOrientation } from 'config';
import menuItem from 'menu-items';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';

import { Menu } from 'menu-items/widget';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { useGetMenu, useGetMenuMaster } from 'api/menu';

// types
import { NavItemType } from 'types';

// ==============================|| SIDEBAR MENU LIST ||============================== //

interface MenuListProps {
  r2mOnly?: boolean;
}

/**
 * Sidebar visibility rules:
 *  - admin users see both the `admin` and `r2m` groups (admin tools + per-hotel features).
 *  - non-admin (hotel) users see only the `r2m` group.
 *  - other Berry-template groups (dashboard, application, forms, …) pass through.
 *
 * Exported so the rule is testable as a pure function.
 */
export function filterMenuByRole(items: NavItemType[], isAdmin: boolean): NavItemType[] {
  return items.filter((item) => {
    if (item.id === 'admin') return isAdmin;
    return true;
  });
}

/**
 * Used by the public-shell / r2m-only layout (e.g. when the hotel selector is the
 * primary affordance). Keeps just the r2m group, plus the admin group for admins.
 */
export function filterMenuR2mOnly(items: NavItemType[], isAdmin: boolean): NavItemType[] {
  return items.filter((item) => item.id === 'r2m' || (item.id === 'admin' && isAdmin));
}

function MenuList({ r2mOnly = false }: MenuListProps) {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    state: { menuOrientation }
  } = useConfig();
  const { menuLoading } = useGetMenu();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const { user } = useAuth();
  const [selectedID, setSelectedID] = useState<string | undefined>('');
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  let widgetMenu = Menu();

  const isAdmin = !!user?.admin;

  useLayoutEffect(() => {
    if (r2mOnly) {
      const items = filterMenuR2mOnly([...menuItem.items], isAdmin);
      setMenuItems({ items });
      return;
    }
    const isFound = menuItem.items.some((element) => {
      if (element.id === 'group-widget') {
        return true;
      }
      return false;
    });
    let items: NavItemType[];
    if (menuLoading) {
      menuItem.items.splice(1, 0, widgetMenu);
      items = [...menuItem.items];
    } else if (!menuLoading && widgetMenu?.id !== undefined && !isFound) {
      menuItem.items.splice(1, 1, widgetMenu);
      items = [...menuItem.items];
    } else {
      items = [...menuItem.items];
    }
    setMenuItems({ items: filterMenuByRole(items, isAdmin) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuLoading, isAdmin, r2mOnly]);

  // last menu-item to show in horizontal menu bar
  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;

  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navItems = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id}>
              <NavItem item={item} level={1} isParents setSelectedID={() => setSelectedID('')} />
              <Activity mode={!isHorizontal && index !== 0 ? 'visible' : 'hidden'}>
                <Divider sx={{ py: 0.5 }} />
              </Activity>
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            selectedID={selectedID}
            item={item}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" align="center" sx={{ color: 'error.main' }}>
            Menu Items Error
          </Typography>
        );
    }
  });

  return !isHorizontal ? <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box> : <>{navItems}</>;
}

export default memo(MenuList);
